import React, { useId, useMemo, useState } from 'react';
import type { AIUsage } from '../../hooks/useAIUsage';
import { AIUsageCard } from './AIUsageCard';

const BETA_INFORMATION_URL = 'https://www.latticelitigation.com/help/beta/';

export type TabId =
  | 'dashboard'
  | 'matter-details'
  | 'stageboard'
  | 'mapper'
  | 'facts'
  | 'case-context'
  | 'authorities'
  | 'docket'
  | 'experts'
  | 'motions'
  | 'parties'
  | 'preferences'
  | 'visualization'
  | 'negotiation'
  | 'create-documents'
  | 'reports'
  | 'ai-intake';

export interface MatterSummary {
  id: string | number;
  display_number?: string;
  description?: string;
  client?: string;
  status?: string;
}

export interface AppSidebarProps {
  showWizard: boolean;
  mattersList: MatterSummary[];
  matterId: string;
  setMatterId: (id: string) => void;
  clioMatter: MatterSummary | null;
  activeTab: TabId;
  hasReachedPleadings: boolean;
  tryNavigate: (target: TabId, action?: () => void) => void;
  setOpenThreadClaimId: (id: string | null) => void;
  showAllMatters: boolean;
  mattersListLoading: boolean;
  moreMattersLoading: boolean;
  onMatterScopeChange: (includeAll: boolean) => void | Promise<void>;
  isEmbedded?: boolean;
  aiUsage?: AIUsage | null;
}

function isRunningInIframe(): boolean {
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
}

function matterLabel(matter: MatterSummary): string {
  return `${matter.display_number ?? matter.id}: ${matter.description ?? 'Unnamed Case'}`;
}

export function AppSidebar({
  showWizard,
  mattersList,
  matterId,
  setMatterId,
  clioMatter,
  activeTab,
  hasReachedPleadings,
  tryNavigate,
  setOpenThreadClaimId,
  showAllMatters,
  mattersListLoading,
  moreMattersLoading,
  onMatterScopeChange,
  isEmbedded = isRunningInIframe(),
  aiUsage = null,
}: AppSidebarProps): React.JSX.Element {
  const listboxId = useId();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [isMatterListOpen, setIsMatterListOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const goto = (target: TabId): void => tryNavigate(target, () => setOpenThreadClaimId(null));

  const selectedMatter = mattersList.find(m => String(m.id) === String(matterId)) ?? clioMatter;

  const filteredMatters = useMemo(() => {
    const query = searchValue.trim().toLocaleLowerCase();
    if (!query) return mattersList;
    return mattersList.filter(m => [m.display_number, m.description, m.client, m.id]
      .some(value => String(value ?? '').toLocaleLowerCase().includes(query)));
  }, [mattersList, searchValue]);

  const handleMatterChange = (newId: string): void => {
    setMatterId(newId);
    const url = new URL(window.location.href);
    url.searchParams.set('matter_id', newId);
    window.history.pushState({}, '', url.toString());
  };

  const selectMatter = (matter: MatterSummary): void => {
    setSearchValue(matterLabel(matter));
    setIsMatterListOpen(false);
    handleMatterChange(String(matter.id));
  };

  const handleSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'Escape') {
      setIsMatterListOpen(false);
      return;
    }
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      setIsMatterListOpen(true);
      const direction = event.key === 'ArrowDown' ? 1 : -1;
      setHighlightedIndex((current): number => Math.max(0, Math.min(filteredMatters.length - 1, current + direction)));
      return;
    }
    if (event.key === 'Enter' && isMatterListOpen && filteredMatters[highlightedIndex]) {
      event.preventDefault();
      selectMatter(filteredMatters[highlightedIndex]);
    }
  };

  return (
    <aside className={`nav app-sidebar ${isCollapsed ? 'collapsed' : ''}`} aria-label="Case navigation" style={{ display: showWizard && isEmbedded ? 'none' : undefined }}>
      <div className="brand app-sidebar-brand">
        <div className="app-sidebar-brand-row">
          <h1>
            <span className="dot"></span>
            <span className="app-sidebar-brand-name">Lattice</span>
            <a
              className="app-beta-flag"
              href={BETA_INFORMATION_URL}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Beta service information (opens in a new tab)"
              title="Read important information about the Lattice beta"
            >Beta</a>
          </h1>
          <button
            type="button"
            className="app-sidebar-collapse"
            aria-label={isCollapsed ? 'Expand navigation' : 'Collapse navigation'}
            aria-expanded={!isCollapsed}
            title={isCollapsed ? 'Expand navigation' : 'Collapse navigation'}
            onClick={() => setIsCollapsed(current => !current)}
          >
            <span aria-hidden="true">{isCollapsed ? '›' : '‹'}</span>
          </button>
        </div>

        {!isEmbedded && (
          <div className="matter-selector-container">
            <label htmlFor="active-matter-search" className="matter-selector-label">Active Case</label>
            <div className="matter-combobox">
              <input
                id="active-matter-search"
                type="text"
                role="combobox"
                aria-autocomplete="list"
                aria-controls={listboxId}
                aria-expanded={isMatterListOpen}
                aria-activedescendant={isMatterListOpen && filteredMatters[highlightedIndex] ? `${listboxId}-${filteredMatters[highlightedIndex].id}` : undefined}
                autoComplete="off"
                value={isMatterListOpen ? searchValue : (selectedMatter ? matterLabel(selectedMatter) : searchValue)}
                placeholder={mattersListLoading ? 'Loading matters…' : 'Search matters…'}
                disabled={mattersListLoading}
                onFocus={() => { setSearchValue(''); setIsMatterListOpen(true); setHighlightedIndex(0); }}
                onBlur={() => window.setTimeout(() => setIsMatterListOpen(false), 100)}
                onChange={(event) => { setSearchValue(event.target.value); setIsMatterListOpen(true); setHighlightedIndex(0); }}
                onKeyDown={handleSearchKeyDown}
              />
              <span className="matter-combobox-chevron" aria-hidden="true">⌄</span>
              {isMatterListOpen && (
                <div id={listboxId} role="listbox" className="matter-options">
                  {filteredMatters.length > 0 ? filteredMatters.map((matter, index) => (
                    <button
                      id={`${listboxId}-${matter.id}`}
                      key={matter.id}
                      type="button"
                      role="option"
                      aria-selected={String(matter.id) === String(matterId)}
                      className={`matter-option ${index === highlightedIndex ? 'highlighted' : ''}`}
                      onMouseDown={(event) => event.preventDefault()}
                      onMouseEnter={() => setHighlightedIndex(index)}
                      onClick={() => selectMatter(matter)}
                    >
                      <span>{matter.display_number ?? matter.id}</span>
                      <small>{matter.description ?? 'Unnamed Case'}{matter.status && showAllMatters ? ` · ${matter.status}` : ''}</small>
                    </button>
                  )) : (
                    <div className="matter-options-empty">No matching matters</div>
                  )}
                </div>
              )}
            </div>

            <label className="matter-scope-toggle">
              <input
                type="checkbox"
                checked={showAllMatters}
                disabled={mattersListLoading}
                onChange={(event) => void onMatterScopeChange(event.target.checked)}
              />
              <span className="matter-scope-track" aria-hidden="true"></span>
              <span>All matters</span>
            </label>

            {moreMattersLoading && (
              <div className="matter-list-progress" role="status" aria-live="polite">
                <span className="matter-list-spinner" aria-hidden="true"></span>
                Loading additional matters…
              </div>
            )}

            {matterId && clioMatter && (
              <div className="matter matter-clio-reference">Clio Mat: #{clioMatter.display_number ?? 'N/A'}</div>
            )}
          </div>
        )}
      </div>

      {!showWizard && (
        <>
          <div className="navsec app-sidebar-case-section">
            <button title="AI Document Intake" className={`navitem ${activeTab === 'ai-intake' ? 'sel' : ''}`} onClick={() => goto('ai-intake')}>
              <span className="ic">✨</span><span className="navitem-label">AI Document Intake</span>
            </button>
            <div className="lblmini">CASE</div>
            <button title="Matter Details" className={`navitem ${activeTab === 'matter-details' ? 'sel' : ''}`} onClick={() => goto('matter-details')}>
              <span className="ic">🗂️</span><span className="navitem-label">Matter Details</span>
            </button>
            <button title="Parties & Counsel" className={`navitem ${activeTab === 'parties' ? 'sel' : ''}`} onClick={() => goto('parties')}><span className="ic">👥</span><span className="navitem-label">Parties &amp; Counsel</span></button>
            <button title="Experts" className={`navitem ${activeTab === 'experts' ? 'sel' : ''}`} onClick={() => goto('experts')}><span className="ic">🧪</span><span className="navitem-label">Experts</span></button>
            <button title="Docket" className={`navitem ${activeTab === 'docket' ? 'sel' : ''}`} onClick={() => goto('docket')}><span className="ic">🗓️</span><span className="navitem-label">Docket</span></button>
          </div>

          <div className="navsec app-sidebar-lenses-section">
            <div className="lblmini">LENSES</div>
            <button title="Dashboard" className={`navitem ${activeTab === 'dashboard' ? 'sel' : ''}`} onClick={() => goto('dashboard')}><span className="ic">📊</span><span className="navitem-label">Dashboard</span></button>
            <button title="Facts" className={`navitem ${activeTab === 'facts' ? 'sel' : ''}`} onClick={() => goto('facts')}><span className="ic">📋</span><span className="navitem-label">Facts</span></button>
            <button title="Case Summary & Context" className={`navitem ${activeTab === 'case-context' ? 'sel' : ''}`} onClick={() => goto('case-context')}><span className="ic">🧭</span><span className="navitem-label">Case Context</span></button>
            <button title="Proof Graph" className={`navitem ${activeTab === 'mapper' ? 'sel' : ''}`} onClick={() => goto('mapper')}><span className="ic">⚖️</span><span className="navitem-label">Proof Graph</span></button>
            <button title="Authority" className={`navitem ${activeTab === 'authorities' ? 'sel' : ''}`} onClick={() => goto('authorities')}><span className="ic">📚</span><span className="navitem-label">Authority</span></button>
            {hasReachedPleadings && (
              <button title="Motions Hub" className={`navitem ${activeTab === 'motions' ? 'sel' : ''}`} onClick={() => goto('motions')}><span className="ic">🏛️</span><span className="navitem-label">Motions Hub</span></button>
            )}
            <button title="Negotiation & Mediation" className={`navitem ${activeTab === 'negotiation' ? 'sel' : ''}`} onClick={() => goto('negotiation')}><span className="ic">🤝</span><span className="navitem-label">Negotiation &amp; Mediation</span></button>
            <button title="Graph Visualizer" className={`navitem ${activeTab === 'visualization' ? 'sel' : ''}`} onClick={() => goto('visualization')}><span className="ic">📊</span><span className="navitem-label">Graph Visualizer</span></button>
          </div>

          <div className="navsec app-sidebar-output-section">
            <div className="lblmini">OUTPUT</div>
            <button title="Create Documents" className={`navitem ${activeTab === 'create-documents' ? 'sel' : ''}`} onClick={() => goto('create-documents')}><span className="ic">📄</span><span className="navitem-label">Create Documents</span></button>
            <button title="Case Reports" className={`navitem ${activeTab === 'reports' ? 'sel' : ''}`} onClick={() => goto('reports')}><span className="ic">📋</span><span className="navitem-label">Case Reports</span></button>
          </div>

        </>
      )}

      <div className="app-sidebar-bottom">
        {!showWizard && (
          <>
            {aiUsage && (
              <AIUsageCard usage={aiUsage} />
            )}
            <div className="app-sidebar-preferences">
              <button title="Preferences" className={`navitem ${activeTab === 'preferences' ? 'sel' : ''}`} onClick={() => goto('preferences')}>
                <span className="ic">⚙️</span><span className="navitem-label">Preferences</span>
              </button>
            </div>
          </>
        )}
        <div className="navfoot app-sidebar-footer">
          <div className="app-sidebar-name">{__APP_NAME__}</div>
          <div className="app-sidebar-version">v{__APP_VERSION__}</div>
        </div>
      </div>
    </aside>
  );
}
